import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { API_BASE } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { canWriteWebsiteContent } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import {
  WEBSITE_IMAGE_FIELDS,
  WEBSITE_SHARED_FIELDS,
  WEBSITE_TEXT_FIELDS,
  publicWebsiteOrigin,
  type WebsiteImageField,
} from "@/lib/websiteContentCatalog";
import {
  fetchWebsiteContent,
  saveWebsiteContent,
  uploadWebsiteImage,
  type WebsiteBlocks,
  type WebsiteContentDocument,
} from "@/lib/websiteContentApi";
import { normalizeBlocks } from "@/lib/websiteTiles";
import { WebsiteTilesEditor } from "@/pages/stammdaten/WebsiteTilesEditor";

type Locale = "de" | "en";
type TextState = Record<string, { de: string; en: string }>;
type ImageState = Record<string, { src: string; altDe: string; altEn: string }>;

function groupsOf<T extends { group: string }>(fields: T[]): Array<[string, T[]]> {
  const groups: Array<[string, T[]]> = [];
  for (const field of fields) {
    const last = groups[groups.length - 1];
    if (!last || last[0] !== field.group) groups.push([field.group, [field]]);
    else last[1].push(field);
  }
  return groups;
}

function emptyDocument(): WebsiteContentDocument {
  return { texts: { de: {}, en: {} }, shared: {}, images: {}, blocks: {} };
}

function previewSrc(src: string, hostname: string): string {
  if (src.startsWith("/api/")) return `${API_BASE}${src}`;
  if (src.startsWith("/images/")) return `${publicWebsiteOrigin(hostname)}${src}`;
  return src;
}

export function WebsiteContentPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canEdit = canWriteWebsiteContent(groups);
  const [locale, setLocale] = useState<Locale>("de");
  const [query, setQuery] = useState("");
  const [loaded, setLoaded] = useState<WebsiteContentDocument>(emptyDocument());
  const [texts, setTexts] = useState<TextState>({});
  const [shared, setShared] = useState<Record<string, string>>({});
  const [images, setImages] = useState<ImageState>({});
  const [blocks, setBlocks] = useState<WebsiteBlocks>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const hostname = typeof window === "undefined" ? "" : window.location.hostname;
  const websiteUrl = `${publicWebsiteOrigin(hostname)}/?bearbeiten=1`;

  const applyDocument = useCallback((document: WebsiteContentDocument) => {
    const nextTexts: TextState = {};
    for (const field of WEBSITE_TEXT_FIELDS) {
      nextTexts[field.key] = {
        de: document.texts.de?.[field.key] ?? field.de,
        en: document.texts.en?.[field.key] ?? field.en,
      };
    }
    const nextShared: Record<string, string> = {};
    for (const field of WEBSITE_SHARED_FIELDS) {
      nextShared[field.key] = document.shared?.[field.key] ?? field.value;
    }
    const nextImages: ImageState = {};
    for (const field of WEBSITE_IMAGE_FIELDS) {
      const image = document.images?.[field.key];
      nextImages[field.key] = {
        src: image?.src || field.src,
        altDe: image?.alt?.de || field.altDe,
        altEn: image?.alt?.en || field.altEn,
      };
    }
    setLoaded(document);
    setTexts(nextTexts);
    setShared(nextShared);
    setImages(nextImages);
    setBlocks(normalizeBlocks(document.blocks));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      applyDocument(await fetchWebsiteContent());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, [applyDocument]);

  useEffect(() => {
    setPageMeta({
      title: "Website",
      subtitle: "Fotos und Kacheln für die Campingplatz-Website",
    });
    void load();
    return () => setPageMeta(null);
  }, [load, setPageMeta]);

  const needle = query.trim().toLowerCase();
  const visibleTexts = WEBSITE_TEXT_FIELDS.filter((field) => matches(field, needle, locale === "de" ? field.de : field.en));
  const visibleShared = WEBSITE_SHARED_FIELDS.filter((field) => matches(field, needle, field.value));
  const visibleImages = WEBSITE_IMAGE_FIELDS.filter((field) => matches(field, needle, field.altDe));

  async function onSave() {
    setSaving(true);
    setError(null);
    setSuccess(false);
    const next: WebsiteContentDocument = {
      texts: { de: { ...loaded.texts.de }, en: { ...loaded.texts.en } },
      shared: { ...loaded.shared },
      images: { ...loaded.images },
      blocks,
    };
    for (const field of WEBSITE_TEXT_FIELDS) {
      for (const lang of ["de", "en"] as const) {
        const value = (texts[field.key]?.[lang] ?? "").trim();
        const fallback = (lang === "de" ? field.de : field.en).trim();
        if (!value || value === fallback) delete next.texts[lang][field.key];
        else next.texts[lang][field.key] = value;
      }
    }
    for (const field of WEBSITE_SHARED_FIELDS) {
      const value = (shared[field.key] ?? "").trim();
      if (!value || value === field.value.trim()) delete next.shared[field.key];
      else next.shared[field.key] = value;
    }
    for (const field of WEBSITE_IMAGE_FIELDS) {
      const image = images[field.key];
      if (!image) continue;
      const alt: Record<string, string> = {};
      if (image.altDe.trim() && image.altDe.trim() !== field.altDe.trim()) alt.de = image.altDe.trim();
      if (image.altEn.trim() && image.altEn.trim() !== field.altEn.trim()) alt.en = image.altEn.trim();
      if (image.src === field.src && Object.keys(alt).length === 0) delete next.images[field.key];
      else next.images[field.key] = { src: image.src, ...(Object.keys(alt).length ? { alt } : {}) };
    }
    try {
      applyDocument(await saveWebsiteContent(next));
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  async function onUpload(field: WebsiteImageField, file: File | undefined) {
    if (!file) return;
    setError(null);
    try {
      const url = await uploadWebsiteImage(file);
      setImages((prev) => ({ ...prev, [field.key]: { ...(prev[field.key] ?? { src: url, altDe: "", altEn: "" }), src: url } }));
      setSuccess(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload fehlgeschlagen");
    }
  }

  return (
    <div className="space-y-6">
      <Card
        title="So ändern Sie die Website"
        description="Legen Sie Kacheln an, laden Sie Fotos hoch und schreiben Sie kurze Texte. Speichern übernimmt die Änderung sofort. Dieselbe Anmeldung gilt für App und Website."
        actions={
          <Button
            type="button"
            variant="secondary"
            onClick={() => window.open(websiteUrl, "_blank", "noopener")}
          >
            Website öffnen
          </Button>
        }
      >
        <p className="text-sm text-label-secondary">
          Unter jedem Bereich steht „Kachel hinzufügen“. Größe „Ganz groß“ zeigt ein Foto über die Breite.
          Englisch können Sie leer lassen, dann erscheint der deutsche Text. Die Preise folgen der Buchhaltung.
        </p>
      </Card>

      {!canEdit ? (
        <p className="text-sm text-label-secondary">
          Sie können die Inhalte ansehen. Zum Speichern brauchen Sie die Rolle Rezeption oder Administration.
        </p>
      ) : null}
      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {success ? <p className="text-sm text-status-green">Gespeichert. Die Website zeigt die Änderungen sofort.</p> : null}

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm">
          Sprache
          <select
            className="ml-2 h-10 rounded-lg border border-separator bg-card px-2"
            value={locale}
            onChange={(event) => setLocale(event.target.value as Locale)}
          >
            <option value="de">Deutsch</option>
            <option value="en">Englisch</option>
          </select>
        </label>
        <input
          className="h-10 min-w-64 flex-1 rounded-lg border border-separator bg-card px-3 text-sm"
          placeholder="Feld suchen"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <Button type="button" onClick={() => void onSave()} loading={saving} disabled={!canEdit || loading}>
          Speichern
        </Button>
      </div>

      {loading ? <p className="text-sm text-label-secondary">Inhalte werden geladen …</p> : null}

      {!loading ? (
        <WebsiteTilesEditor
          blocks={blocks}
          document={loaded}
          locale={locale}
          canEdit={canEdit}
          hostname={hostname}
          onChange={(next) => {
            setBlocks(next);
            setSuccess(false);
          }}
          onUpload={async (file) => {
            setError(null);
            try {
              return await uploadWebsiteImage(file);
            } catch (err) {
              setError(err instanceof Error ? err.message : "Upload fehlgeschlagen");
              throw err;
            }
          }}
        />
      ) : null}

      {!loading &&
        groupsOf(visibleShared).map(([group, fields]) => (
          <FieldGroup key={group} title={group} open={Boolean(needle) || group === "Kontaktdaten"}>
            {fields.map((field) => (
              <TextControl
                key={field.key}
                label={field.label}
                value={shared[field.key] ?? ""}
                disabled={!canEdit}
                onChange={(value) => {
                  setShared((prev) => ({ ...prev, [field.key]: value }));
                  setSuccess(false);
                }}
              />
            ))}
          </FieldGroup>
        ))}

      {!loading &&
        groupsOf(visibleTexts).map(([group, fields]) => (
          <FieldGroup key={`${locale}-${group}`} title={group} open={Boolean(needle)}>
            {fields.map((field) => (
              <TextControl
                key={field.key}
                label={field.label}
                multiline={field.multiline}
                value={texts[field.key]?.[locale] ?? ""}
                disabled={!canEdit}
                onChange={(value) => {
                  setTexts((prev) => ({
                    ...prev,
                    [field.key]: { de: prev[field.key]?.de ?? field.de, en: prev[field.key]?.en ?? field.en, [locale]: value },
                  }));
                  setSuccess(false);
                }}
              />
            ))}
          </FieldGroup>
        ))}

      {!loading &&
        groupsOf(visibleImages).map(([group, fields]) => (
          <FieldGroup key={group} title={group} open={Boolean(needle)}>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {fields.map((field) => {
                const image = images[field.key];
                const alt = locale === "de" ? image?.altDe ?? "" : image?.altEn ?? "";
                return (
                  <label key={field.key} className="block text-sm">
                    <span className="font-medium text-label">{field.label}</span>
                    <img
                      src={previewSrc(image?.src || field.src, hostname)}
                      alt=""
                      className="mt-2 aspect-[4/3] w-full border border-separator object-cover"
                    />
                    <input
                      className="mt-2 block w-full text-sm"
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      disabled={!canEdit}
                      onChange={(event) => void onUpload(field, event.target.files?.[0])}
                    />
                    <input
                      className="mt-2 h-10 w-full rounded-lg border border-separator bg-card px-3"
                      value={alt}
                      disabled={!canEdit}
                      aria-label={`${field.label} Bildtext`}
                      onChange={(event) => {
                        const value = event.target.value;
                        setImages((prev) => {
                          const current = prev[field.key] ?? { src: field.src, altDe: field.altDe, altEn: field.altEn };
                          return {
                            ...prev,
                            [field.key]: locale === "de" ? { ...current, altDe: value } : { ...current, altEn: value },
                          };
                        });
                        setSuccess(false);
                      }}
                    />
                  </label>
                );
              })}
            </div>
          </FieldGroup>
        ))}
    </div>
  );
}

function matches(field: { label: string; key: string; group: string }, needle: string, sample: string): boolean {
  if (!needle) return true;
  return `${field.group} ${field.label} ${field.key} ${sample}`.toLowerCase().includes(needle);
}

function FieldGroup({ title, children, open }: { title: string; children: ReactNode; open?: boolean }) {
  return (
    <details open={open} className="rounded-xl border border-separator bg-card">
      <summary className="cursor-pointer px-5 py-4 text-base font-semibold text-label">{title}</summary>
      <div className="space-y-4 border-t border-separator px-5 py-4">{children}</div>
    </details>
  );
}

function TextControl({
  label,
  value,
  multiline = false,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  multiline?: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const className = "mt-1 w-full rounded-lg border border-separator bg-card px-3 py-2 text-sm";
  return (
    <label className="block">
      <span className="text-sm font-medium text-label">{label}</span>
      {multiline ? (
        <textarea className={className} rows={4} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
      ) : (
        <input className={`${className} h-10`} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
      )}
    </label>
  );
}
